import React, { Component } from "react";
import debug from "debug";
import { isEmpty, isEqual } from "lodash";
import { IconButton, Paper, Popover } from "@material-ui/core";
import { Delete, FolderMove, FolderUpload } from "mdi-material-ui";
import {
  Customer,
  CustomerType,
  NodeType,
  RemoveCustomerAvatar,
  UploadCustomerAvatar,
  UploadCustomerBgPic
} from "@graphql";
import { store } from "@store";
import { Link } from "react-router-dom";
import locale, { toLocale } from "@bonliva-crm/locale";
import ContactIcon from "@material-ui/icons/PermContactCalendar";
import NodeCallList from "@containers/NodeCallList/NodeCallList";
import { Body, Spinner, Title, WithTooltip } from "@components";
import ItemCard from "@components/ItemCard/ItemCard";
import { CustomerChip, getCustomerColor } from "@components/CustomerChip";
import CustomerParents from "@components/CustomerParents";
import { CustomerContactList } from "../CustomerContactList/CustomerContactList";
import { CustomerProfileLocation } from "../../navigation/locations/CustomerProfileLocation";
import IconTitle from "@common/ItemCard/IconTitle";
import { CustomerDeleteButton } from "../customer-delete-button/customer-delete-button";

const log = debug("app:customers:CustomerCard");

const MAX_AOES = 5;

export type CustomerCardProps = {
  customerGroup?: string;
  highlightSearch?: string;
  /** on card body click */
  onClick?: (e: React.SyntheticEvent<any>) => any;
  onCloseGroup?: () => any;
  onDelete?: () => void;
  onMove?: () => void;
  onPromoteToOrg?: () => void;
  cantMoveCustomersReasons?: string[];
  onEdit?: (e: React.SyntheticEvent<any>) => any;
  onParentClick?: (id: string) => void;
  onResponsiblePersonClick?: (e: React.SyntheticEvent<any>) => void;
  selected?: boolean;
  style?: React.CSSProperties;
  includeContactPersons?: boolean;
  withAvatarUpload?: boolean;
  withBgUpload?: boolean;
  withCallLists?: boolean; // show call list star
  withDelete?: boolean;
  asLink?: boolean;
};

export type Props = CustomerCardProps & {
  data: Partial<Customer>;
  error?: any;
  loading?: boolean;
};

type State = {
  isCallListPopoverOpen: boolean;
  showAllAoes: boolean;
};

class CustomerCard extends Component<Props, State> {
  callListsPopperAnchor = null;

  static defaultProps: Partial<Props> = {
    onParentClick: (id) =>
      store().navigation.open(`customer_${id}`, new CustomerProfileLocation(id))
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      isCallListPopoverOpen: false,
      showAllAoes: false
    };
  }

  shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>) {
    const result =
      this.state.showAllAoes !== nextState.showAllAoes ||
      this.props.selected !== nextProps.selected ||
      this.props.customerGroup !== nextProps.customerGroup ||
      !isEqual(this.props.data, nextProps.data) ||
      this.props.highlightSearch !== nextProps.highlightSearch;

    const resultPopover =
      this.state.isCallListPopoverOpen !== nextState.isCallListPopoverOpen;

    return result || resultPopover;
  }

  toggle = (): void => {
    this.setState({ showAllAoes: !this.state.showAllAoes });
    this.forceUpdate();
  };

  handleAvatarClick = () => {
    const { data, withAvatarUpload } = this.props;

    if (!withAvatarUpload) {
      return;
    }

    store()
      .upload.requestImageUploadAsUrl()
      .then(store().upload.requestImageCrop)
      .then((file) => {
        if (!file) {
          return;
        }

        UploadCustomerAvatar.func({
          id: data._id,
          file
        }).catch((e) => store().snackbars.error(`Cannot upload image\nReason: ${e}`));
      })
      .catch((e) => {
        store().snackbars.error(`Upload failed\nReason: ${e}`);
      });
  };

  handleAvatarRemove = () => {
    const { data } = this.props;

    RemoveCustomerAvatar.func({ id: data._id }).catch((e) => {
      store().snackbars.error(`Operation failed\nReason: ${e}`);
    });
  };

  handleBgUploadClick = (e: React.SyntheticEvent<any>) => {
    e.preventDefault();
    e.stopPropagation();

    const { data, withBgUpload } = this.props;

    if (!withBgUpload) {
      return;
    }

    store()
      .upload.requestImageUploadAsUrl()
      .then(store().upload.requestImageCrop)
      .then((file) => {
        if (!file) {
          return;
        }

        UploadCustomerBgPic.func({
          id: data._id,
          file
        }).catch((e) => {
          store().snackbars.error(`Upload failed\nReason: ${e}`);
        });
      })
      .catch((e) => {
        store().snackbars.error(`Upload failed\nReason: ${e}`);
      });
  };

  addMoveBtn = (icon: React.ReactNode, key: string, label: string, cb: () => void) => {
    const { cantMoveCustomersReasons } = this.props;

    if (!cb) {
      return null;
    }

    const title = isEmpty(cantMoveCustomersReasons)
      ? label
      : cantMoveCustomersReasons.join(", ");

    return (
      <WithTooltip key={key} title={title}>
        <IconButton
          disabled={!isEmpty(cantMoveCustomersReasons)}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            cb();
          }}
        >
          {icon}
        </IconButton>
      </WithTooltip>
    );
  };

  deleteButton = () => {
    const { data, onDelete, withDelete } = this.props;

    if (!onDelete || withDelete) {
      return null;
    }

    return (
      <CustomerDeleteButton key="delete" customerId={data._id} onDelete={onDelete} />
    );
  };

  deleteButtonExt = () => {
    if (!this.props.withDelete) {
      return null;
    }

    return (
      <IconButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          this.props.onDelete();
        }}
      >
        <Delete />
      </IconButton>
    );
  };

  render() {
    const {
      data,
      loading,
      error,
      onClick = () =>
        store().navigation.open(
          `customer_${this.props.data._id}`,
          new CustomerProfileLocation(this.props.data._id)
        ),
      onParentClick, // keep it
      withAvatarUpload,
      withBgUpload,
      includeContactPersons, // keep it
      withCallLists,
      ...rest
    } = this.props;

    if (loading) {
      return <Spinner />;
    }

    if (error) {
      return null;
    }

    const city = data.Address.City;

    let renderTitle;

    if (this.props.customerGroup) {
      renderTitle = () => (
        <div style={{ display: "flex" }}>
          <Title onClick={this.props.onCloseGroup} className="highlighter">
            {data.Name || locale.missing}
          </Title>
          <Title style={{ margin: "0 8px" }}>/</Title>
          <Title>{this.props.customerGroup}</Title>
        </div>
      );
    }

    const itemCard = (
      <>
        <ItemCard
          avatarColor={getCustomerColor(data.Type)}
          avatarTooltip={withAvatarUpload ? locale["Click to upload a new image"] : null}
          avatarDeleteTooltip={
            withAvatarUpload ? locale["Click to delete an avatar"] : null
          }
          onAvatarClick={this.handleAvatarClick}
          onAvatarRemove={this.handleAvatarRemove}
          onAddBgClick={withBgUpload ? this.handleBgUploadClick : undefined}
          entity={data}
          title={data.Name}
          renderTitle={renderTitle}
          chips={
            <CustomerChip style={{ marginLeft: 8, marginRight: 8 }} type={data.Type} />
          }
          details={this.renderDetails()}
          subTitle={data.Type === CustomerType.Region ? "" : city}
          subheading={this.renderSubheading()}
          keyValues={[
            { key: locale.ExpertiseWithDots, value: this.renderAOEs() },
            { key: `${locale.system}:`, value: data.journalSystem },
            {
              key: locale.Specialization,
              value: data.Specializations.join(", ")
            },
            { key: " ", value: this.renderLastContact() }
          ]}
          // responsiblePerson={!details.includes("responsibleUser")}
          showStar={withCallLists ? (data.inLists ? "full" : "hollow") : null}
          rightControls={[
            this.addMoveBtn(
              <FolderUpload />,
              "promote",
              locale.PromoteCustomerToOrganisation,
              this.props.onPromoteToOrg
            ),
            this.addMoveBtn(
              <FolderMove />,
              "move",
              locale.MoveCustomer,
              this.props.onMove
            ),
            this.deleteButton(),
            this.deleteButtonExt()
          ]}
          onStarClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            this.callListsPopperAnchor = e.currentTarget;
            this.setState({
              isCallListPopoverOpen: true
            });
            // after opening and closing an overlay, @observer stops working
            // difficult to say why.. so forceUpdate "fixes" the problem
            this.forceUpdate();
          }}
          onClick={onClick}
          data-test="CustomerCard"
          data-test-id={data._id}
          {...rest}
        />
        {this.renderCallListsPopper()}
      </>
    );

    if (this.props.asLink) {
      return (
        <Link
          to={new CustomerProfileLocation(this.props.data._id).toURI()}
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "block"
          }}
        >
          {itemCard}
        </Link>
      );
    }

    return itemCard;
  }

  private renderAOEs() {
    const { data } = this.props;

    const extra = data.areasOfExpertiseTotal - MAX_AOES;

    const aoes = !this.state.showAllAoes
      ? data.AreasOfExpertise.slice(0, MAX_AOES)
      : data.AreasOfExpertise;

    return (
      <span>
        <span>{aoes.join(", ")}</span>
        {extra > 0 && !this.state.showAllAoes && (
          <Body color="secondary">&nbsp;+{extra}</Body>
        )}
      </span>
    );
  }

  private renderLastContact() {
    const { LastContactDate: lastContactDate } = this.props.data;

    return (
      <div style={{ flexDirection: "row", display: "flex" }}>
        <IconTitle icon={<ContactIcon />} title={locale.LastContact}>
          {lastContactDate ? <Body>{toLocale.date(lastContactDate)}</Body> : "n/a"}
        </IconTitle>
      </div>
    );
  }

  private renderSubheading = () => {
    const { data, onParentClick } = this.props;

    return (
      <CustomerParents
        parents={data.Parents}
        onClick={(e, id) => {
          e.preventDefault();
          onParentClick(id);
        }}
      />
    );
  };

  private renderDetails = () => {
    const { data, includeContactPersons } = this.props;
    if (!includeContactPersons || data.StaffingContactPersons?.length === 0) {
      return null;
    }
    return <CustomerContactList data={data.StaffingContactPersons} />;
  };

  private renderCallListsPopper = () => {
    log("renderCallListPopper");
    const { data } = this.props;

    if (!this.state.isCallListPopoverOpen) {
      return null;
    }

    const component = (
      <Paper>
        <NodeCallList
          node={{
            NodeID: data._id,
            NodeType: NodeType.Customer,
            Name: data.Name
          }}
        />
      </Paper>
    );

    return (
      <Popover
        open
        anchorEl={this.callListsPopperAnchor}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClose={() => {
          this.setState({ isCallListPopoverOpen: false }, () => {
            this.callListsPopperAnchor = null;
          });
        }}
      >
        {component}
      </Popover>
    );
  };
}

export default CustomerCard;
